import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { CallStatus } from '../../lib/enums';
import lang from '../../lib/language';
import { PROCESS_CANCEL_MODAL_DELAY_MS, PROCESS_FINISHED_MODAL_DELAY_MS } from '../../lib/libSettings';

import BaseModal from '../../shared_components/BaseModal/BaseModal';
import Button from '../../shared_components/Button/Button';

import ModalContent from './ModalBody';

import styles from './ProcessFinishedModal.module.scss';

const MODAL_WIDTH = 424;

type MessageStructure = {
  title: string;
  body: string;
}

type Props = {
  symbol: string;
  processFailedMessage: string;
  processFailedMessageSub?: string;
  processSuccessMessageTitle: string;
  toggleModal: (arg?: any) => void;
  isSelfClosing?: boolean;
  callStatus?: CallStatus;
  isCancelOrder?: boolean;
  processSuccessMessageBody?: string;
  defaultMessageTitle?: string;
  defaultMessageBody?: string;
  processPendingMessage?: MessageStructure;
  processPartiallyRejectedMessage?: MessageStructure;
  processRejectedMessage?: MessageStructure;
};

const ProcessFinishedModal = (
  {
    symbol,
    callStatus,
    toggleModal,
    processFailedMessage,
    processFailedMessageSub,
    processSuccessMessageBody,
    processSuccessMessageTitle,
    isSelfClosing = true,
    isCancelOrder = false,
    processPendingMessage,
    processPartiallyRejectedMessage,
    processRejectedMessage,
    defaultMessageTitle,
    defaultMessageBody,
  }: Props,
): JSX.Element => {
  const [modalOpened, setModalOpened] = useState<boolean>(true);
  const isRejected = Boolean(processRejectedMessage);

  useEffect(() => {
    if (!isSelfClosing || isRejected) return undefined;

    const timer = setTimeout(() => {
      setModalOpened(false);
    }, isCancelOrder ? PROCESS_CANCEL_MODAL_DELAY_MS : PROCESS_FINISHED_MODAL_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isRejected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BaseModal
      isOpen={modalOpened}
      width={isRejected ? undefined : MODAL_WIDTH}
      contentClassName={styles.modalContent}
      noPadding={isRejected}
      toggle={() => toggleModal(null)}
    >
      <div className={classNames(styles.content, { [styles.rejectedContent]: isRejected })}>
        <div className={styles.contentWrapper}>
          <ModalContent
            symbol={symbol}
            callStatus={callStatus}
            processFailedMessage={processFailedMessage}
            processFailedMessageSub={processFailedMessageSub}
            defaultMessageBody={defaultMessageBody}
            defaultMessageTitle={defaultMessageTitle}
            processPendingMessage={processPendingMessage}
            processSuccessMessageTitle={processSuccessMessageTitle}
            processSuccessMessageBody={processSuccessMessageBody}
            processPartiallyRejectedMessage={processPartiallyRejectedMessage}
            processRejectedMessage={processRejectedMessage}
          />
          {isRejected && (
            <Button className={styles.okayButton} biggerFontSize fullWidth onClick={() => setModalOpened(false)}>
              {lang.commonOkay()}
            </Button>
          )}
        </div>
      </div>
    </BaseModal>
  );
};

export default ProcessFinishedModal;
